import { compact } from 'lodash';
import { useEffect, useRef } from 'react';

import { DISABLED_DOCUMENT_TYPES } from '@/modules/documents/documents.constants';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { useHouseholdDocumentsQuery } from '@/modules/documents/MultiDocumentUploader/graphql/MultiDocumentUploader.generated';
import { DocumentType } from '@/types/schema';

interface UsePreselectHouseholdDocumentsProps {
  householdId: string | null | undefined;
  preselectedExistingDocumentIds?: string[];
  excludedDocumentTypes?: DocumentType[];
}

export function usePreselectHouseholdDocuments({
  householdId,
  preselectedExistingDocumentIds,
  excludedDocumentTypes = [],
}: UsePreselectHouseholdDocumentsProps) {
  const { setSelectedExistingDocuments } = useMultiDocumentUploaderContext();
  const hasInitialized = useRef(false);

  const { data: householdDocumentsData } = useHouseholdDocumentsQuery({
    variables: { householdId: householdId ?? '' },
    skip: !householdId,
  });

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    // Don't process until data is loaded
    if (!householdDocumentsData?.documents) {
      return;
    }

    const allHouseholdDocuments = compact(
      householdDocumentsData.documents.edges?.map((edge) => {
        const node = edge?.node;
        if (!node) return null;
        return node;
      })
    );

    const selectableDocuments = allHouseholdDocuments.filter(
      (doc) =>
        !DISABLED_DOCUMENT_TYPES.has(doc.type) &&
        !excludedDocumentTypes.includes(doc.type)
    );

    if (preselectedExistingDocumentIds?.length) {
      const preselectedDocs = selectableDocuments.filter((doc) =>
        preselectedExistingDocumentIds.includes(doc.id)
      );

      setSelectedExistingDocuments(
        preselectedDocs.map((doc) => ({
          id: doc.id,
          displayName: doc.name,
        }))
      );
      hasInitialized.current = true;
      return;
    }

    if (selectableDocuments.length > 0) {
      setSelectedExistingDocuments(
        selectableDocuments.map((doc) => ({
          id: doc.id,
          displayName: doc.name,
        }))
      );
      hasInitialized.current = true;
    }
  }, [
    excludedDocumentTypes,
    preselectedExistingDocumentIds,
    setSelectedExistingDocuments,
    householdDocumentsData,
  ]);
}
